import { ArrowBack } from '@mui/icons-material';
import { Box, Breadcrumbs, IconButton, Link, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

export interface DetailBreadcrumb {
  label: ReactNode;
  to?: string;
}

export interface DetailHeaderProps {
  title: ReactNode;
  sectionIcon?: ReactNode;
  entityId?: string | null;
  subtitle?: ReactNode;
  breadcrumbs?: DetailBreadcrumb[];
  actions?: ReactNode;
  onBack?: () => void;
  backHref?: string;
  backLabel?: string;
  backDisabled?: boolean;
  sx?: SxProps<Theme>;
}

export default function DetailHeader({
  title,
  sectionIcon,
  entityId,
  subtitle,
  breadcrumbs,
  actions,
  onBack,
  backHref,
  backLabel,
  backDisabled,
  sx,
}: DetailHeaderProps) {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();

  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    const hasHistory = typeof window !== 'undefined' && window.history.length > 1;
    if (hasHistory) {
      navigate(-1);
      return;
    }

    if (backHref) {
      navigate(backHref);
      return;
    }

    navigate(-1);
  };

  const renderBreadcrumb = (crumb: DetailBreadcrumb, index: number) => {
    const isLast = index === mergedBreadcrumbs.length - 1;
    const breadcrumbBaseSx = {
      typography: 'caption',
      display: 'inline-flex',
      alignItems: 'center',
    };
    const breadcrumbSx = {
      ...breadcrumbBaseSx,
      ...(isLast && {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 'calc(100vw - 120px)',
      }),
    };

    if (crumb.to && !isLast) {
      return (
        <Link
          key={`${crumb.label}-${index}`}
          component={RouterLink}
          to={crumb.to}
          underline="hover"
          color="text.secondary"
          sx={breadcrumbSx}
        >
          {crumb.label}
        </Link>
      );
    }

    return (
      <Typography
        key={`${crumb.label}-${index}`}
        color={isLast ? 'text.primary' : 'text.secondary'}
        sx={breadcrumbSx}
      >
        {crumb.label}
      </Typography>
    );
  };

  const mergedBreadcrumbs = [
    { label: t('common:menu.dashboard'), to: '/' },
    ...(breadcrumbs ?? []),
  ];

  return (
    <Box component="header" sx={{ mb: 3, width: '100%', ...sx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: 1.5,
          mb: 3,
        }}
      >
        <Tooltip title={backLabel || t('common:actions.back')}>
          <span>
            <IconButton
              onClick={handleBack}
              disabled={backDisabled}
              size="small"
              sx={{ p: 0 }}
            >
              <ArrowBack sx={{ fontSize: '14px' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: 'calc(100vw - 100px)',
            '& .MuiBreadcrumbs-ol': {
              flexWrap: 'nowrap',
              overflow: 'hidden',
              minWidth: 0,
            },
          }}
        >
          {mergedBreadcrumbs.map(renderBreadcrumb)}
        </Breadcrumbs>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mt: 1,
          flexWrap: 'wrap',
        }}
      >
        {isMobile && actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {actions}
          </Box>
        )}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {sectionIcon}
            <Typography variant="h4" fontWeight={700}>
              {title}
            </Typography>
          </Box>
          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, ml: sectionIcon ? 4 : 0 }}
            >
              {subtitle}
            </Typography>
          ) : entityId ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', mt: 0.5, ml: sectionIcon ? 4 : 0 }}
            >
              {entityId}
            </Typography>
          ) : null}
        </Box>
        {!isMobile && actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}
